import React, { useCallback, useMemo, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Refresh, Settings } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import { fetchMetricsHistory, fetchStats } from "@/lib/api";
import { UdpPipelinePanel } from "@/components/UdpPipelinePanel";
import {ApexChart, rotateColors} from "@/components/ApexChart";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import {Switch} from "@/components/ui/switch";
import {Label} from "@/components/ui/label";
import { formatDuration, formatNumber } from "@voltie/localization-web";
import { useCatalog, type Translate } from "@/app/localization";
import { HttpError } from "@/lib/http";
import { viewerInstant } from "@/app/displayFormat";

type WindowUnit = "minutes" | "hours" | "days";

const DURATIONS: { amount: number; unit: WindowUnit; minutes: number }[] = [
  { amount: 5, unit: "minutes", minutes: 5 },
  { amount: 15, unit: "minutes", minutes: 15 },
  { amount: 30, unit: "minutes", minutes: 30 },
  { amount: 1, unit: "hours", minutes: 60 },
  { amount: 6, unit: "hours", minutes: 360 },
  { amount: 12, unit: "hours", minutes: 720 },
  { amount: 24, unit: "hours", minutes: 1440 },
  { amount: 3, unit: "days", minutes: 4320 },
  { amount: 7, unit: "days", minutes: 10080 },
  { amount: 30, unit: "days", minutes: 43200 },
  { amount: 90, unit: "days", minutes: 129600 },
  { amount: 365, unit: "days", minutes: 525600 },
];

/** "5m", "1h", "30d"; a duration outside the list is shown in minutes. */
function windowLabel(t: Translate, minutes: number): string {
  const d = DURATIONS.find((x) => x.minutes === minutes);
  return t("operator.ops.metrics.window", d ? { unit: d.unit, amount: d.amount } : { unit: "minutes", amount: minutes });
}

type ChartGroup = "core" | "queues" | "correlation" | "disk_io" | "telemetry_archive";

/**
 * `id` is the panel's stable identity: the hidden-chart choices in localStorage are
 * stored by it, so it keeps the original English title. Shown texts come from
 * `operator.ops.metrics.title_<slug>` / `desc_<slug>`.
 */
const CHART_PANELS: { id: string; slug: string; keys: string[]; group: ChartGroup; described?: boolean }[] = [
  // Core
  // "Recv vs Proc Rate" occupies the old "Messages / sec" slot: that single-series
  // chart drew only `messages_per_second`, which is already this chart's second
  // (processed) series from the same backend metric, so it was fully redundant.
  { id: "Recv vs Proc Rate", slug: "recv_vs_proc", keys: ["received_per_second", "messages_per_second"], group: "core", described: true },
  { id: "Active Units", slug: "active_units", keys: ["active_units"], group: "core" },
  { id: "Charging", slug: "charging", keys: ["charging_units"], group: "core" },
  { id: "Total Power (kW)", slug: "total_power", keys: ["total_power_kw"], group: "core" },
  { id: "Queue Depth", slug: "queue_depth", keys: ["queue_depth"], group: "core", described: true },
  { id: "Unsupported Units", slug: "unsupported_units", keys: ["unsupported_units"], group: "core" },
  // Queue processing
  { id: "WUP Queue Depth", slug: "wup_queue_depth", keys: ["wup_queue_depth"], group: "queues", described: true },
  { id: "UDP Gate Self-Heal Queue", slug: "gate_selfheal_queue", keys: ["gate_eval_queue_peak", "gate_eval_queue_depth"], group: "queues", described: true },
  { id: "UDP Gate Self-Heal Outcomes (/sec)", slug: "gate_selfheal_outcomes", keys: ["gate_eval_evaluated_per_second", "gate_eval_no_orchestrator_per_second", "gate_eval_timed_out_per_second", "gate_eval_failed_per_second"], group: "queues", described: true },
  { id: "Session Effect Queue", slug: "effect_queue", keys: ["effect_queue_depth"], group: "queues", described: true },
  // Correlation / delay
  { id: "Session Rate (/sec)", slug: "session_rate", keys: ["sessions_per_second", "session_events_per_second"], group: "correlation", described: true },
  { id: "Effect Processing Rate", slug: "effect_rate", keys: ["effects_per_second"], group: "correlation", described: true },
  { id: "Session Save Latency (ms)", slug: "session_save_latency", keys: ["session_save_avg_ms", "session_save_p95_ms"], group: "correlation", described: true },
  { id: "Session Latency Samples Missed", slug: "session_latency_missed", keys: ["session_save_latency_samples_missed"], group: "correlation", described: true },
  { id: "UDP Worker Stalls (/sec)", slug: "worker_stalls", keys: ["worker_stalls_per_second"], group: "correlation", described: true },
  { id: "Drop Rate (/sec)", slug: "drop_rate", keys: ["messages_dropped_rate", "gate_eval_dropped_rate", "wup_dropped_rate"], group: "correlation", described: true },
  { id: "UDP Gate Self-Heal Evaluation Latency (ms)", slug: "gate_selfheal_latency", keys: ["gate_eval_avg_ms"], group: "correlation", described: true },
  // Disk I/O
  { id: "Disk I/O Rate (/sec)", slug: "disk_io_rate", keys: ["log_writes_per_second", "db_writes_per_second"], group: "disk_io", described: true },
  { id: "Log Writer Queue", slug: "log_writer_queue", keys: ["log_queue_depth"], group: "disk_io", described: true },
  // Telemetry archive (Rust stream backend)
  { id: "Archiver Lag / Pending", slug: "archiver_lag", keys: ["archiver_lag", "archiver_pending"], group: "telemetry_archive", described: true },
  { id: "Archiver Writes (/sec)", slug: "archiver_writes", keys: ["archiver_writes_per_second"], group: "telemetry_archive", described: true },
  { id: "Archive Stream Length", slug: "archive_stream_length", keys: ["archiver_stream_length"], group: "telemetry_archive", described: true },
];

const METRIC_NAMES = CHART_PANELS.flatMap((p) => p.keys);

/** Series name: a readable label for metrics whose raw name is unclear, else the raw
 *  name with underscores as spaces (operator.ops.metrics.series lists the readable ones). */
function seriesName(t: Translate, key: string): string {
  return t("operator.ops.metrics.series", { metric: key, raw: key.replace(/_/g, " ") });
}

const MAX_CHART_POINTS = 720;

const VISIBILITY_KEY = "evse-metrics-chart-visibility";

function loadHiddenCharts(): Set<string> {
  try {
    const stored = localStorage.getItem(VISIBILITY_KEY);
    if (stored) return new Set(JSON.parse(stored));
  } catch { /* ignore */ }
  return new Set();
}

function saveHiddenCharts(hidden: Set<string>) {
  localStorage.setItem(VISIBILITY_KEY, JSON.stringify([...hidden]));
}

/**
 * Y-axis scaling mode, applied live to every chart on the page:
 *  - "auto":   ApexCharts fits the full data min/max (default behavior).
 *  - "zero":   axis floored at 0, top auto-fit — magnitude shown true-to-zero.
 *  - "robust": axis fits the central ~95% (p2.5–p97.5) so rare outliers clip
 *              instead of flattening the visible range. Tooltip still shows the
 *              real value, so clipped spikes are one hover away.
 */
type YScale = "auto" | "zero" | "robust";

const YSCALE_KEY = "evse-metrics-yscale";
const YSCALE_OPTIONS: YScale[] = ["auto", "zero", "robust"];

function loadYScale(): YScale {
  const stored = localStorage.getItem(YSCALE_KEY);
  return stored === "zero" || stored === "robust" ? stored : "auto";
}

/**
 * Compute fixed y-axis bounds for one panel's pooled values under the given
 * mode. Returns {} for "auto" (let ApexCharts decide) or when there's no data.
 * Cheap: a single sort of <= MAX_CHART_POINTS numbers, run once per data refresh.
 */
function computeBounds(values: number[], mode: YScale): { yMin?: number; yMax?: number } {
  if (mode === "auto" || values.length === 0) return {};
  if (mode === "zero") return { yMin: 0 };
  // robust: clip to the central band, then pad ~8% so the line isn't glued to the edge.
  const sorted = [...values].sort((a, b) => a - b);
  const at = (qt: number) => sorted[Math.min(sorted.length - 1, Math.floor(qt * (sorted.length - 1)))];
  const lo = at(0.025);
  const hi = at(0.975);
  const pad = (hi - lo) * 0.08 || Math.abs(hi) * 0.08 || 1;
  return { yMin: lo - pad, yMax: hi + pad };
}

export function MetricsPage() {
  const { t, catalog } = useCatalog();
  const [duration, setDuration] = useState(60);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [hiddenCharts, setHiddenCharts] = useState<Set<string>>(loadHiddenCharts);
  const [yScale, setYScale] = useState<YScale>(loadYScale);

  const toggleChart = useCallback((id: string) => {
    setHiddenCharts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveHiddenCharts(next);
      return next;
    });
  }, []);

  const q = useQuery({
    queryKey: ["metrics", "history", duration],
    queryFn: () => fetchMetricsHistory(duration),
    refetchInterval: autoRefresh ? 10_000 : false,
    placeholderData: keepPreviousData,
  });

  // The fleet /api/stats snapshot (admin page): the UDP pipeline panel's only source.
  const statsQ = useQuery({
    queryKey: ["stats", "fleet", "udp-pipeline"],
    queryFn: () => fetchStats(),
    refetchInterval: autoRefresh ? 10_000 : false,
    placeholderData: keepPreviousData,
  });
  const fleetStats = statsQ.data as Record<string, unknown> | undefined;

  const metricsRaw = q.data?.metrics ?? {};
  const stats = q.data?.stats ?? {};
  const isStale = !!q.data?.stale;

  // Decimate to <= MAX_CHART_POINTS, tracking the REAL source index of each kept
  // sample so value lookups stay aligned. (Previously the always-appended final
  // point was read at decimatedIdx*step, which overshot the array and fell back
  // to 0 — fabricating a trailing zero that dragged the y-axis min down to 0.)
  const { timestamps, indices } = useMemo(() => {
    const rawTs: number[] = (metricsRaw.timestamps ?? []).map((ts: string) => new Date(ts).getTime());
    const s = rawTs.length > MAX_CHART_POINTS ? Math.ceil(rawTs.length / MAX_CHART_POINTS) : 1;
    const idx: number[] = [];
    for (let i = 0; i < rawTs.length; i += s) idx.push(i);
    // Always include the most recent sample, even if it isn't on the stride.
    if (idx.length && idx[idx.length - 1] !== rawTs.length - 1) idx.push(rawTs.length - 1);
    return { timestamps: idx.map((i) => rawTs[i]), indices: idx };
  }, [metricsRaw.timestamps]);

  const hasData = timestamps.length > 0;

  const activePanels = useMemo(
    () =>
      CHART_PANELS.filter(
        (panel) =>
          !hiddenCharts.has(panel.id) &&
          panel.keys.some(
            (key) => Array.isArray(metricsRaw[key]) && metricsRaw[key].length > 0,
          ),
      ),
    [metricsRaw, hiddenCharts],
  );

  const panelSeries = useMemo(
    () =>
      activePanels.map((panel) =>
        panel.keys.map((key) => {
          const arr = metricsRaw[key] as (number | null)[] | undefined;
          return {
            name: seriesName(t, key),
            // A missing value is a hole in the line, never a measured 0.
            data: indices.map((srcIdx, k) => [timestamps[k], arr?.[srcIdx] ?? null] as [number, number | null]),
          };
        }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activePanels, timestamps, indices, catalog, ...METRIC_NAMES.map((k) => metricsRaw[k])],
  );

  // Fixed y-axis bounds per panel for the active scaling mode. Pools all of a
  // panel's series so multi-series charts share one comparable scale. Recomputed
  // only when the series or mode change (i.e. once per ~10s refresh), never on hover.
  const panelBounds = useMemo(
    () =>
      panelSeries.map((seriesList) => {
        const values = seriesList.flatMap((s) =>
          s.data.flatMap(([, v]) => (v === null ? [] : [v])));
        return computeBounds(values, yScale);
      }),
    [panelSeries, yScale],
  );

  const flatStats = useMemo(() => {
    const result: { key: string; value: string }[] = [];
    for (const [k, v] of Object.entries(stats)) {
      if (typeof v === "number") {
        result.push({ key: k, value: Number.isFinite(v) ? formatNumber(v, Number.isInteger(v) ? 0 : 2) : String(v) });
      } else if (typeof v === "string") {
        const d = new Date(v);
        if (!isNaN(d.getTime()) && /\d{4}-\d{2}-\d{2}T/.test(v)) {
          result.push({ key: k, value: viewerInstant(v, "dateTimeSeconds") ?? v });
        } else {
          result.push({ key: k, value: v });
        }
      }
    }
    return result;
  }, [stats]);

  const groups = useMemo(() => {
    const seen = new Set<ChartGroup>();
    return CHART_PANELS.reduce<ChartGroup[]>((acc, p) => {
      if (!seen.has(p.group)) { seen.add(p.group); acc.push(p.group); }
      return acc;
    }, []);
  }, []);

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1>{t("operator.nav.metrics")}</h1>
          <div className="text-sm opacity-70">{t("operator.ops.metrics.subtitle")}</div>
        </div>
        <div className="flex items-center gap-2">
          <Label>{t("operator.ops.metrics.auto_refresh")}</Label>
          <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh} size="sm" aria-label={t("operator.ops.auto_refresh")} />
          <Button variant={"outline"} size="sm" onClick={() => q.refetch()}>
            <Refresh className="h-4 w-4" /> {t("common.action.refresh")}
          </Button>
          <Button variant={showSettings ? "default" : "outline"} size="sm" onClick={() => setShowSettings((v) => !v)}>
            <Settings className="h-4 w-4" /> {t("operator.ops.metrics.charts")}
          </Button>

          <Label>{t("operator.ops.metrics.y_axis")}</Label>
          <ToggleGroup
            type="single"
            size="sm"
            variant="outline"
            value={yScale}
            onValueChange={(v) => {
              if (!v) return;
              setYScale(v as YScale);
              localStorage.setItem(YSCALE_KEY, v);
            }}
          >
            {YSCALE_OPTIONS.map((o) => (
              <ToggleGroupItem key={o} value={o}>
                {t("operator.ops.metrics.y_scale", { mode: o })}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>

          <ToggleGroup type="single" size="sm" variant="outline" value={String(duration)} onValueChange={(v) => v && setDuration(Number(v))}>
            {DURATIONS.map((d) => (
                <ToggleGroupItem key={d.minutes} value={String(d.minutes)}>
                  {windowLabel(t, d.minutes)}
                </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>

      {isStale && (
        <div className="rounded-md border border-warning/50 bg-warning/10 px-3 py-2 text-sm text-warning-content">
          {t("operator.ops.diagnostics_stale", { detail: "none", reason: "" })}
        </div>
      )}

      {fleetStats ? (
        <UdpPipelinePanel
          stats={fleetStats}
          snapshotTs={typeof fleetStats.snapshot_ts === "string" ? fleetStats.snapshot_ts : null}
        />
      ) : statsQ.isError ? (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {t("operator.ops.metrics.udp_unavailable", { endpoint: "/api/stats" })}
        </div>
      ) : null}

      {/* Chart visibility settings */}
      {showSettings && (
        <Card>
          <CardContent>
            <div className="flex items-center justify-between mb-3">
              <CardTitle className="text-sm">{t("operator.ops.metrics.visibility")}</CardTitle>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" className="text-xs h-6"
                  onClick={() => { setHiddenCharts(new Set()); saveHiddenCharts(new Set()); }}>
                  {t("operator.ops.metrics.show_all")}
                </Button>
                <Button variant="ghost" size="sm" className="text-xs h-6"
                  onClick={() => { const all = new Set(CHART_PANELS.map((p) => p.id)); setHiddenCharts(all); saveHiddenCharts(all); }}>
                  {t("operator.ops.metrics.hide_all")}
                </Button>
              </div>
            </div>
            {groups.map((group) => (
              <div key={group} className="mb-2">
                <div className="text-xs font-medium opacity-50 uppercase mb-1">{t("operator.ops.metrics.group", { group })}</div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1">
                  {CHART_PANELS.filter((p) => p.group === group).map((panel) => (
                    <label key={panel.id} className="flex items-center gap-2 cursor-pointer">
                      <Switch
                        size="sm"
                        checked={!hiddenCharts.has(panel.id)}
                        onCheckedChange={() => toggleChart(panel.id)}
                      />
                      <span className="text-xs">{t(`operator.ops.metrics.title_${panel.slug}`)}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}


        {/* Stats summary */}
        {flatStats.length > 0 && (
            <Card>
              <CardContent>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                  {metricsRaw.tier && (
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-xs uppercase opacity-50">{t("operator.ops.metrics.data_tier")}</span>
                      <span className="uppercase">{metricsRaw.tier}</span>
                    </div>
                  )}
                  {flatStats.map((s) => (
                      <div key={s.key} className="flex items-baseline gap-1.5">
                        <span className="text-xs uppercase opacity-50">{s.key.replace(/_/g, " ")}:</span>
                        <span>{s.value}</span>
                      </div>
                  ))}
                </div>
              </CardContent>
            </Card>
        )}



      {/* Charts */}
      {q.isLoading ? (
        <div className="flex justify-center p-12">
          <span role="status" aria-label={t("common.state.loading")} className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
        </div>
      ) : q.isError ? (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {q.error instanceof HttpError && q.error.status === 503
            ? t("operator.ops.metrics.collector_unavailable")
            : t("operator.ops.metrics.load_failed")}
        </div>
      ) : !hasData ? (
        <Card>
          <CardContent className="text-center opacity-60">{t("operator.ops.metrics.empty")}</CardContent>
        </Card>
      ) : (
        <div className={`grid gap-4 md:grid-cols-2 transition-opacity duration-300 ${q.isFetching ? "opacity-60" : "opacity-100"}`}>
          {activePanels.map((panel, idx) => (
            <Card key={panel.id}>
              <CardContent>
                <CardTitle className="text-sm">{t(`operator.ops.metrics.title_${panel.slug}`)}</CardTitle>
                {panel.described && <div className="text-xs opacity-50 mt-0.5 mb-1">{t(`operator.ops.metrics.desc_${panel.slug}`)}</div>}
                <ApexChart
                  type="area"
                  series={panelSeries[idx]}
                  colors={rotateColors(idx*2)}
                  yMin={panelBounds[idx]?.yMin}
                  yMax={panelBounds[idx]?.yMax}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="text-center text-xs opacity-50">
        {t("operator.ops.metrics.footer", {
          count: timestamps.length,
          window: windowLabel(t, duration),
          auto: autoRefresh ? "on" : "off",
          interval: formatDuration(10, "seconds"),
        })}
      </div>
    </div>
  );
}
